import { computed } from 'vue';
import { useLocalStorage, StorageSerializers } from '@vueuse/core';

// Sparse localStorage: the key stays absent until the user consciously changes the value
// away from its default, and is removed again if they reset it back. Plain useLocalStorage
// instead writes the default to storage on initialization, creating entries for settings
// the user never touched. Internally, null is the stored sentinel for "still at default";
// the getter maps it back to defaultValue and the setter maps defaultValue back to null.
// Limitation: equality uses ===, so defaultValue must be a primitive.
//
// The serializer is derived from the type of defaultValue, not from the (null) initial
// value: passing null would make useLocalStorage fall back to the generic "any" serializer,
// which round-trips everything as raw strings — so a stored boolean false reads back as the
// truthy string "false". Picking the serializer explicitly keeps booleans/numbers intact.
export default function useSparseLocalStorage<T>(key: string, defaultValue: T) {
  const serializer = StorageSerializers[typeof defaultValue as keyof typeof StorageSerializers] ?? StorageSerializers.any;
  const storage = useLocalStorage<T | null>(key, null, { serializer });
  return computed({
    get: () => storage.value ?? defaultValue,
    set: (val: T) => { storage.value = val === defaultValue ? null : val; }
  });
}
